import { CoreImageView } from '@core/widgets';
import { useBehavior } from '@libs/hooks';
import { TWidgetProps } from '@libs/types';
import clsx from 'clsx';
import React, { Activity } from 'react';
import { TCardView } from '../../types/data';
import { ECardView } from '../../types/enums';
import { getInlineStyles } from '../../utils/styles';
import { getHref } from '../Image/imageUtils';
import { VisualView } from '../Image/VisualView';
import { TextCommon } from '../UI/Text/TextCommon';
import styles from './cardView.module.css';

const chunkString = (str: string | number): Array<string> => {
    if (!str) {
        return [];
    }
    return (
        str
            .toString()
            .replace(/\*/g, '•')
            .match(/.{1,4}/g) || []
    );
};

export const CardView = ({ widget }: TWidgetProps<TCardView>) => {
    const { bankImage, bankTitle, pan, accountNumber, expirationDate, paymentSystem, status, messages } = widget.data ?? {};

    const { isDisabled, isHidden } = useBehavior(widget);
    const isDisabledStatus = status === ECardView.DISABLED || status === ECardView.ACCOUNT_NUMBER_DISABLED || isDisabled;
    const isAccountView = status === ECardView.ACCOUNT_NUMBER_ACTIVE || status === ECardView.ACCOUNT_NUMBER_DISABLED;

    const mainTextColor = isDisabledStatus ? 'grey_40_lc' : 'black';
    const secondaryTextColor = isDisabledStatus ? 'grey_40_lc' : 'grey_72_lc';

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <div
                data-test={widget.uid}
                className={clsx(styles.card, isDisabledStatus && styles.disabled)}
                style={getInlineStyles({ margin: widget.options?.margin })}
                role="group"
                aria-hidden={isHidden}
                aria-disabled={isDisabledStatus}
                aria-label={`${bankTitle} ${isAccountView ? accountNumber : pan}`}
            >
                <div data-test={'bankNameAndLogo_' + widget.uid} className={styles.header}>
                    <VisualView
                        avatar={bankImage}
                        iconVariant={'circle'}
                        defaultIconName={'ic_bank'}
                        iconHierarchy={'secondary'}
                        isDisabled={isDisabledStatus}
                        rightMargin
                    />
                    <TextCommon isWidget={false} as="span" content={bankTitle} options={{ variant: 'main', textColor: mainTextColor }} />
                </div>
                {isAccountView ? (
                    <div data-test={'accountView_' + widget.uid} className={styles.accountView}>
                        <TextCommon
                            isWidget={false}
                            as="span"
                            content={messages.accountNumberPlaceholder}
                            options={{ variant: 'secondary', textColor: secondaryTextColor }}
                        />
                        <TextCommon
                            isWidget={false}
                            as="span"
                            content={chunkString(accountNumber).join(' ')}
                            options={{ variant: 'main', textColor: mainTextColor }}
                        />
                    </div>
                ) : (
                    <div data-test={'panView_' + widget.uid} className={styles.panView}>
                        <div data-test={'pan_' + widget.uid} className={styles.container}>
                            {pan &&
                                chunkString(pan).map((str, index) => (
                                    <TextCommon
                                        isWidget={false}
                                        key={`pan-${index}`}
                                        as="span"
                                        content={str}
                                        options={{
                                            variant: 'mCardNo',
                                            textColor: mainTextColor,
                                        }}
                                    />
                                ))}
                        </div>
                        <div data-test={'expDate_' + widget.uid} className={styles.footer}>
                            <TextCommon
                                isWidget={false}
                                as="span"
                                content={`${messages.expDate?.toUpperCase()} ${expirationDate}`}
                                options={{ variant: 'secondary', textColor: secondaryTextColor }}
                            />
                            {paymentSystem && (
                                <CoreImageView
                                    src={getHref(paymentSystem)}
                                    defaultIcon={getHref('paysystem_mastercard')}
                                    styleProps={{
                                        width: 'auto',
                                        height: 'auto',
                                        iconColor: `var(--color-grey_40_lc)`,
                                    }}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </Activity>
    );
};
